import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import type { Lang } from "@/i18n";
import { SITE, path } from "@/lib/site";
import { Reveal } from "../Reveal";
import { useStats } from "@/lib/usePublicContent";
import alexandre from "@/assets/alexandre.jpg";
import { Marquee } from "../Marquee";
import { LiveBadge } from "../LiveBadge";
import { AnimatedNumber } from "../AnimatedNumber";
import { MagneticButton } from "../MagneticButton";

export function HomePage({ lang }: { lang: Lang }) {
  const { t } = useTranslation();
  const fallbackStats = t("home.stats", { returnObjects: true }) as { v: string; l: string }[];
  const subtexts = t("home.statsSubtext", { returnObjects: true }) as string[];
  const { data: dbStats } = useStats(lang);
  const stats =
    dbStats && dbStats.length > 0
      ? dbStats.map((s) => ({ v: s.value, l: s.label }))
      : fallbackStats;
  const offers = t("home.offers", { returnObjects: true }) as {
    title: string;
    desc: string;
    tags: string[];
  }[];
  const method = t("home.method", { returnObjects: true }) as { t: string; d: string }[];
  const forWho = t("home.forWho", { returnObjects: true }) as string[];
  const offerIds = ["operations", "consulting", "tech"];

  return (
    <>
      {/* HERO — Brutalist split */}
      <section className="relative overflow-hidden border-b-[1.5px] border-foreground">
        <div className="grain" aria-hidden />
        <div
          aria-hidden
          className="hatch float-y pointer-events-none absolute -top-10 -right-10 h-48 w-48 opacity-20 md:h-72 md:w-72"
        />
        <div className="container-page relative pt-14 pb-16 md:pt-20 md:pb-24">
          <Reveal delay={0.04}>
            <LiveBadge />
          </Reveal>

          <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-12">
            <Reveal delay={0.08} className="min-w-0 lg:col-span-8">
              <h1 className="font-display whitespace-pre-line font-black uppercase leading-[1.05] tracking-[-0.045em] text-foreground text-[clamp(1.75rem,6vw,5rem)]">
                <span className="text-outline outline-drift inline-block">{t("home.heroTitlePre")}</span>
                <span className="bg-blaze px-2 py-0.5 text-[#0a0a0a]">
                  {t("home.heroTitleAccent")}
                </span>
                {t("home.heroTitlePost")}
              </h1>
            </Reveal>
            <Reveal delay={0.14} className="min-w-0 lg:col-span-4">
              <div className="bento-tile h-full p-6 md:p-7">
                <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
                  / Brief
                </span>
                <p className="mt-4 text-base leading-relaxed text-foreground/85">
                  {t("home.heroSub")}
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <MagneticButton>
                    <Link
                      to={path(lang, "contact")}
                      className="btn-blaze inline-flex items-center gap-2 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em]"
                    >
                      {t("common.bookCall")} <ArrowUpRight size={14} />
                    </Link>
                  </MagneticButton>
                  <MagneticButton strength={0.25}>
                    <Link
                      to={path(lang, "services")}
                      className="btn-ghost-ink inline-flex items-center gap-2 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em]"
                    >
                      {t("common.discoverServices")}
                    </Link>
                  </MagneticButton>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STATS — Bento mosaic */}
      <section className="border-b-[1.5px] border-foreground bg-background">
        <div className="container-page py-10 md:py-14">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
            {stats.map((s, i) => {
              const variant =
                i === 0 ? "is-ink" : i === 2 ? "is-spark" : "";
              const span =
                i === 0 ? "col-span-2 md:col-span-2 md:row-span-2" : "";
              return (
                <Reveal key={i} delay={i * 0.05} className={span}>
                  <div
                    className={`bento-tile ${variant} flex h-full flex-col p-6 md:p-7 ${
                      i === 0 ? "md:p-10" : ""
                    }`}
                  >
                    <AnimatedNumber
                      value={s.v}
                      className={`font-display font-black leading-none tracking-[-0.04em] ${
                        i === 0
                          ? "text-6xl md:text-8xl"
                          : "text-4xl md:text-5xl"
                      }`}
                    />
                    <div
                      className={`mt-4 text-sm font-semibold uppercase tracking-[0.06em] leading-snug ${
                        i === 0 ? "opacity-70" : "opacity-75"
                      }`}
                    >
                      {s.l}
                    </div>
                    {subtexts[i] && (
                      <div className="mt-auto pt-3 text-xs leading-snug opacity-55">
                        {subtexts[i]}
                      </div>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* MARQUEE — Typographic ticker */}
      <Marquee
        variant="ink"
        items={[
          "Hotel operations",
          "Pre-opening",
          "Revenue management",
          "Short-stay & hybrid",
          "Brussels · Luxembourg",
        ]}
      />

      {/* OFFERS — Bento (1 large + 2 stacked) */}
      <section className="section border-b-[1.5px] border-foreground">
        <div className="container-page">
          <Reveal>
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div>
                <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
                  / Solutions — 03
                </span>
                <h2 className="font-display mt-3 max-w-3xl text-4xl font-black uppercase leading-[0.95] tracking-[-0.035em] md:text-7xl">
                  {t("home.offersTitle")}
                </h2>
              </div>
              <p className="max-w-md text-base text-foreground/70">
                {t("home.offersSub")}
              </p>
            </div>
          </Reveal>

          <div className="mt-12 grid gap-4 md:grid-cols-3 md:gap-5">
            {/* Large featured tile */}
            <Reveal delay={0.05} className="md:col-span-2 md:row-span-2">
              <Link
                to={path(lang, "services")}
                hash={offerIds[0]}
                className="bento-tile tile-fx is-hover group flex h-full flex-col p-8 md:p-12"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs font-semibold uppercase tracking-[0.18em] opacity-60">
                    01 / 03
                  </span>
                  <span className="bg-blaze px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#0a0a0a]">
                    Flagship
                  </span>
                </div>
                <h3 className="font-display mt-10 text-3xl font-black uppercase leading-[0.95] tracking-[-0.035em] md:text-6xl">
                  {offers[0]?.title}
                </h3>
                <p className="mt-6 text-base leading-relaxed opacity-80 whitespace-pre-line md:text-lg max-w-xl">
                  {offers[0]?.desc}
                </p>
                <div className="mt-auto pt-8 flex items-end justify-between gap-6 flex-wrap">
                  <div className="flex flex-wrap gap-2">
                    {offers[0]?.tags.map((tag) => (
                      <span
                        key={tag}
                        className="border-[1.5px] border-current px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <span className="font-display inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] transition-all group-hover:gap-3">
                    {t("common.learnMore")}
                  </span>
                </div>
              </Link>
            </Reveal>

            {/* Two stacked tiles */}
            {offers.slice(1).map((o, i) => (
              <Reveal key={i} delay={(i + 1) * 0.06}>
                <Link
                  to={path(lang, "services")}
                  hash={offerIds[i + 1]}
                  className="bento-tile tile-fx is-hover group flex h-full flex-col p-7 md:p-8"
                >
                  <span className="font-mono text-xs font-semibold uppercase tracking-[0.18em] opacity-60">
                    0{i + 2} / 03
                  </span>
                  <h3 className="font-display mt-6 text-2xl font-black uppercase leading-[1] tracking-[-0.03em] md:text-3xl">
                    {o.title}
                  </h3>
                  <p className="mt-4 text-[14px] leading-relaxed opacity-80 whitespace-pre-line">
                    {o.desc}
                  </p>
                  <div className="mt-auto pt-6 flex items-end justify-between gap-4 flex-wrap">
                    <div className="flex flex-wrap gap-1.5">
                      {o.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="border border-current px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em]"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Counter-marquee (reverse, blaze) */}
      <Marquee
        variant="blaze"
        reverse
        items={[
          "pre-opening · opening · daily operations",
          "ADR · RevPAR · GOP",
          "Hotels · Aparthotels · Hybrid",
          "Belgium · Luxembourg",
          "Operations · Consulting · Tech",
        ]}
      />

      {/* MANIFESTO — Oversized typographic statement */}
      <section className="relative overflow-hidden border-b-[1.5px] border-foreground bg-background py-20 md:py-32">
        <div
          aria-hidden
          className="hatch-blaze pointer-events-none absolute inset-0 opacity-15"
        />
        <div className="container-page relative">
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="h-2.5 w-2.5 bg-blaze" aria-hidden />
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-foreground/60">
                / Manifesto — 02:00 AM
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display mt-8 font-black uppercase leading-[1.05] tracking-[-0.05em] text-foreground text-[clamp(2rem,8vw,7rem)]">
              {(() => {
                const titleParts = t("home.manifestoTitle", { returnObjects: true }) as string[];
                return (
                  <>
                    <span className="block">{titleParts[0]}</span>
                    <span className="block">{titleParts[1]}</span>
                    <span className="block">
                      {titleParts[2]?.split(" ").slice(0, -1).join(" ")}{" "}
                      <span className="bg-blaze px-3 text-[#0a0a0a]">
                        {titleParts[2]?.split(" ").pop()}
                      </span>
                    </span>
                  </>
                );
              })()}
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-12 grid gap-8 md:grid-cols-12 md:items-end">
              <p className="md:col-span-7 text-lg leading-relaxed text-foreground/80 md:text-xl whitespace-pre-line">
                {t("home.manifestoDesc")}
              </p>
              <div className="md:col-span-5 md:justify-self-end">
                <Link
                  to={path(lang, "about")}
                  className="font-display inline-flex items-center gap-3 border-b-[1.5px] border-foreground pb-2 text-sm font-bold uppercase tracking-[0.14em] text-foreground hover:gap-4 transition-all"
                >
                  {t("home.readMore")} <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* METHOD — Numbered bento strip */}
      <section className="section border-b-[1.5px] border-foreground bg-foreground text-background">
        <div className="container-page">
          <Reveal>
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-background/60">
              / Méthode
            </span>
            <h2 className="font-display mt-3 max-w-3xl text-4xl font-black uppercase leading-[0.95] tracking-[-0.035em] md:text-7xl">
              {t("home.methodTitle")}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {method.map((m, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="border-[1.5px] border-background bg-foreground p-7 h-full transition-colors hover:bg-background hover:text-foreground">
                  <div className="flex items-center justify-between">
                    <span className="font-display text-5xl font-black tracking-[-0.04em]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="h-2 w-2 bg-blaze" aria-hidden />
                  </div>
                  <h3 className="font-display mt-8 text-xl font-bold uppercase tracking-[-0.01em]">
                    {m.t}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed opacity-75">{m.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOR WHO — Mixed bento chips */}
      <section className="section border-b-[1.5px] border-foreground">
        <div className="container-page">
          <Reveal>
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
              / Pour qui
            </span>
            <h2 className="font-display mt-3 whitespace-nowrap text-3xl font-black uppercase leading-[1] tracking-[-0.035em] sm:text-4xl md:text-5xl lg:text-6xl">
              {t("home.forWhoTitle")}
            </h2>
          </Reveal>
          <ul className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 items-stretch">
            {forWho.map((label, i) => {
              const accent = i % 5 === 2 ? "is-spark" : i % 7 === 4 ? "is-ink" : "";
              return (
                <li key={i} className="h-full">
                  <Reveal delay={i * 0.04} className="h-full">
                    <div className={`bento-tile ${accent} flex h-full min-h-[112px] items-center gap-4 px-6 py-6`}>
                      <span className="font-mono text-xs font-bold opacity-60">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-[15px] font-medium">{label}</span>
                    </div>
                  </Reveal>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ABOUT TEASER — Asymmetric split */}
      <section className="section border-b-[1.5px] border-foreground bg-[var(--surface)]">
        <div className="container-page grid items-center gap-10 sm:grid-cols-12 sm:gap-12">
          <Reveal className="col-span-5 sm:col-span-4 lg:col-span-3">
            <div className="relative">
              <div className="aspect-[4/5] max-w-[280px] overflow-hidden border-[1.5px] border-foreground">
                <img
                  src={alexandre}
                  alt="Alexandre Dufrene"
                  className="h-full w-full object-cover grayscale"
                  loading="lazy"
                />
              </div>
              <span
                aria-hidden
                className="absolute -bottom-3 -right-3 h-12 w-12 bg-blaze border-[1.5px] border-foreground"
              />
            </div>
          </Reveal>
          <Reveal delay={0.08} className="col-span-7 sm:col-span-8 lg:col-span-9">
            <div>
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
                / {SITE.name}
              </span>
              <h2 className="font-display mt-3 text-3xl font-black uppercase leading-[1] tracking-[-0.035em] sm:text-4xl md:text-6xl">
                {t("home.aboutTeaserTitle")}
              </h2>
              <p className="mt-8 text-base leading-[1.7] text-foreground/85 md:text-lg">
                {t("home.aboutTeaser")}
              </p>
              <Link
                to={path(lang, "about")}
                className="font-display mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-foreground hover:gap-3 transition-all"
              >
                <span className="inline-block h-2 w-2 bg-blaze" aria-hidden />
                {t("common.moreAboutAlex")} <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA — Brutalist block */}
      <section className="section relative overflow-hidden bg-foreground text-background">
        <div className="container-page relative">
          <div className="grid gap-10 md:grid-cols-12 md:items-end">
            <Reveal className="md:col-span-8">
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-background/60">
                / {t("common.bookCall")}
              </span>
              <h2 className="font-display mt-4 text-4xl font-black uppercase leading-[0.92] tracking-[-0.04em] md:text-8xl">
                {t("home.finalTitle")}
              </h2>
              <p className="mt-6 max-w-xl text-base text-background/75 md:text-lg">
                {t("home.finalSub")}
              </p>
            </Reveal>
            <Reveal delay={0.08} className="md:col-span-4">
              <div className="flex flex-col gap-3">
                <MagneticButton strength={0.4}>
                  <Link
                    to={path(lang, "contact")}
                    className="btn-blaze inline-flex w-full items-center justify-between gap-2 px-6 py-4 text-sm font-bold uppercase tracking-[0.12em]"
                  >
                    {t("common.bookSlot")} <ArrowUpRight size={18} />
                  </Link>
                </MagneticButton>
                <a
                  href={`mailto:${SITE.email}`}
                  className="inline-flex items-center justify-between gap-2 border-[1.5px] border-background px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-background/90 transition hover:bg-background hover:text-foreground"
                >
                  {SITE.email} <ArrowUpRight size={16} />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
